/**
 * Pack hall calls that need the network (the server makes the pack lot code and QR link; a
 * dispatch is never queued — the vehicle waits until it is saved). Typed with zod at the boundary.
 *
 *   POST /pack-runs/{id}/start   (F7.2-c)  → run + pack lot (code, dates, QR link, fixed for life)
 *   POST /dispatches             (F7.8-a)  → a trip of released pack lots to a buyer
 *
 * REST bodies are camelCase with kilos as decimal strings (uc-core house style); the phone keeps
 * its own snake_case, integer-gram shapes and converts them here (startRequest, dispatchRequest).
 *
 * On a phone loaded with the demo data (development builds only) the same calls are answered on
 * the phone, so the whole hall is clickable without a backend.
 */
import { z } from 'zod'
import { api } from '@/api'
import { env } from '@/lib/env'
import { istDateKey, istYYMMDD } from '@/lib/ist'
import {
  downloadPackBundle,
  getMeta,
  nextDailyCounter,
  patchSitePack,
  packLotSchema,
  type PackLot,
  type PackRun,
} from '@/offline'
import { uploadMedia } from '@/offline/media'
import { httpTransport } from '@/offline/transport'

const startResponseSchema = z.looseObject({
  run: z.looseObject({ id: z.string(), code: z.string(), started_at: z.string() }),
  pack_lot: packLotSchema,
})
export type StartResponse = z.infer<typeof startResponseSchema>

export interface StartBody {
  cleanout_photo_id: string
  allergen_clean_confirmed: boolean
  leftover_g: number
  scale_check: { equipment_id: string; reference_g: number; reading_g: number }
  label_version_id: string
}

const dispatchResponseSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  status: z.string(),
  items: z
    .array(
      z.looseObject({
        pack_lot_id: z.string().nullish(),
        pack_lot_code: z.string().nullish(),
        code: z.string().nullish(),
        pouches: z.number().int(),
        pouches_left: z.number().int().nullish(),
      }),
    )
    .default([]),
})
export type DispatchResponse = z.infer<typeof dispatchResponseSchema>

export interface DispatchBody {
  kind: 'DISPATCH'
  from_location_id: string
  to_org_id: string
  challan_no: string
  vehicle_no: string
  driver_name: string | null
  driver_phone: string | null
  vehicle_clean: true
  items: Array<{ pack_lot_code: string; pouches: number }>
}

/** POST /pack-runs/{id}/start body. */
export function startRequest(body: StartBody) {
  return {
    cleanoutPhotoId: body.cleanout_photo_id,
    allergenCleanConfirmed: body.allergen_clean_confirmed,
    leftoverKg: (body.leftover_g / 1000).toFixed(3),
    scaleCheck: {
      equipmentId: body.scale_check.equipment_id,
      referenceG: body.scale_check.reference_g,
      readingG: body.scale_check.reading_g,
    },
    labelVersionId: body.label_version_id,
  }
}

/** POST /dispatches body. */
export function dispatchRequest(body: DispatchBody) {
  return {
    fromLocationId: body.from_location_id,
    toOrgId: body.to_org_id,
    challanNo: body.challan_no,
    vehicleNo: body.vehicle_no,
    ...(body.driver_name ? { driverName: body.driver_name } : {}),
    ...(body.driver_phone ? { driverPhone: body.driver_phone } : {}),
    vehicleClean: body.vehicle_clean,
    items: body.items.map((i) => ({ packLotCode: i.pack_lot_code, pouches: i.pouches })),
  }
}

/** A phone set up with the development demo data answers pack-hall calls itself. */
export async function isDemoPhone(): Promise<boolean> {
  if (!env.isDev) return false
  const reg = await getMeta('device')
  return !!reg?.team.id.startsWith('dev-')
}

type RunRef = Pick<PackRun, 'id' | 'code' | 'sku'>

const CHECK_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'

function addDays(dateKey: string, days: number): string {
  const d = new Date(`${dateKey}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/** Demo answer: a pack lot shaped like the server's (its check letter is not a real one). */
function demoPackLot(run: RunRef, n: number, now: number): PackLot {
  const today = istDateKey(now)
  const [y, m, d] = today.split('-')
  const seq = String(30 + n).padStart(4, '0')
  const base = `PL-${y}-${m}${d}-${seq}`
  let h = 0
  for (const ch of base) h = (h * 31 + ch.charCodeAt(0)) % 1_000_003
  const code = `${base}-${CHECK_ALPHABET[h % CHECK_ALPHABET.length]}`
  const gtin = run.sku.gtin ? run.sku.gtin.padStart(14, '0') : null
  return {
    id: `dev-pl-${seq}`,
    code,
    run_id: run.id,
    sku_id: run.sku.id,
    sku_name: run.sku.name,
    net_g: run.sku.net_g,
    pack_date: today,
    best_before: addDays(today, run.sku.shelf_life_days ?? 180),
    qr_url: gtin
      ? `https://id.uncompromised.in/01/${gtin}/10/${code}`
      : `https://id.uncompromised.in/10/${code}`,
    status: 'PACKING',
    hold: 'NONE',
    pouch_count: null,
    pouches_dispatched: 0,
  }
}

/** F7.2 Start run: the server fixes the pack lot; the phone keeps it with the run for offline use. */
export async function startPackRun(run: RunRef, body: StartBody): Promise<StartResponse> {
  let res: StartResponse
  if (await isDemoPhone()) {
    const n = await nextDailyCounter('demo-pack-lot')
    const now = Date.now()
    res = {
      run: { id: run.id, code: run.code, started_at: new Date(now).toISOString() },
      pack_lot: demoPackLot(run, n, now),
    }
  } else {
    // The cleanout photo must be on the server before the run can start.
    await uploadMedia(httpTransport.uploadMedia, { ids: [body.cleanout_photo_id], force: true })
    res = await api.post(`/pack-runs/${encodeURIComponent(run.id)}/start`, startRequest(body), {
      schema: startResponseSchema,
    })
  }
  await patchSitePack((p) => ({
    ...p,
    pack_runs: p.pack_runs.map((r) =>
      r.id === run.id
        ? {
            ...r,
            code: res.run.code,
            started_at: res.run.started_at,
            label_version_id: body.label_version_id,
            pack_lot: res.pack_lot,
          }
        : r,
    ),
    pack_lots: [...p.pack_lots.filter((l) => l.id !== res.pack_lot.id), res.pack_lot],
  }))
  if (!(await isDemoPhone())) await downloadPackBundle(run.id).catch(() => undefined)
  return res
}

/** F7.8 Dispatch: one trip of released pouches to a buyer. */
export async function dispatchToBuyer(body: DispatchBody): Promise<DispatchResponse> {
  let res: DispatchResponse
  if (await isDemoPhone()) {
    const n = await nextDailyCounter('demo-dispatch')
    res = {
      id: `dev-ds-${n}`,
      code: `DS-${istYYMMDD()}-${String(n).padStart(2, '0')}`,
      status: 'IN_TRANSIT',
      items: body.items.map((i) => ({ pack_lot_code: i.pack_lot_code, pouches: i.pouches })),
    }
  } else {
    res = await api.post('/dispatches', dispatchRequest(body), { schema: dispatchResponseSchema })
  }
  await patchSitePack((p) => ({
    ...p,
    pack_lots: p.pack_lots.map((l) => {
      const item = body.items.find((i) => i.pack_lot_code === l.code)
      return item ? { ...l, pouches_dispatched: l.pouches_dispatched + item.pouches } : l
    }),
  }))
  return res
}
